/**
 * Page Gate – Container + inner <page-gate> from metaobject URL.
 *
 * <page-gate-container>  Rendered in theme.liquid before #layout-content-container.
 *                         When the gate applies, sets body.page-gate--locked so the
 *                         layout is CSS-hidden, fetches <page-gate> from the metaobject
 *                         URL, and clears the lock on unlock (no template/DOM swap).
 *
 * <page-gate>             Rendered by the metaobject section; dispatches unlock events.
 */

/* =============================================================================
    PageGateContainer
============================================================================= */

if (!customElements.get("page-gate-container")) {
  class PageGateContainer extends HTMLElement {
    /** @type {Object} */
    #data = {};

    /** @type {AbortController|null} */
    #abortController = null;

    /** @type {boolean} */
    #debug = false;

    connectedCallback() {
      this.#data = this.#consumeJsonData();
      this.#debug = this.#data.debug || false;

      const storageKey = `page-gate-${this.#data.gate_id}`;

      if (this.#getStorage(storageKey)) {
        this.#log("Gate already passed via localStorage");
        this.remove();
        return;
      }

      this.#log("Gate not passed, fetching gate UI");
      document.body.classList.add("page-gate--locked");
      this.addEventListener("page-gate:unlocked", this.#handleUnlock);
      this.#fetchGate();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.removeEventListener("page-gate:unlocked", this.#handleUnlock);
      if (document.body.classList.contains("page-gate--locked")) {
        document.body.classList.remove("page-gate--locked");
      }
    }

    /**
     * Reads the JSON data script, parses it, removes it from the DOM.
     * @returns {Object}
     */
    #consumeJsonData() {
      const el = this.querySelector('script[type="application/json"]');
      if (!el) return {};

      try {
        const data = JSON.parse(el.textContent);
        el.remove();
        return data;
      } catch {
        return {};
      }
    }

    /**
     * Fetches the metaobject page, extracts <page-gate> from #main-content,
     * clears the container, and injects the gate UI.
     */
    async #fetchGate() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      try {
        const response = await fetch(this.#data.gate_url, {
          signal: this.#abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Fetch failed: ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const mainContent = doc.querySelector("#main-content");

        if (!mainContent) {
          throw new Error("#main-content not found in response");
        }

        const gate = mainContent.querySelector("page-gate");

        if (!gate) {
          throw new Error("page-gate element not found in #main-content");
        }

        this.innerHTML = "";
        this.appendChild(gate);
        this.classList.remove("page-gate-container--loading");
        this.classList.add("page-gate-container--loaded");
        this.#log("Gate UI loaded");
      } catch (error) {
        if (error.name === "AbortError") return;

        console.error("[page-gate-container]", error);
        this.#log("Fetch failed, revealing page as fallback");
        this.#revealPage();
      }
    }

    /** @param {CustomEvent} event */
    #handleUnlock = (event) => {
      this.#setStorage(`page-gate-${this.#data.gate_id}`, "true");
      const redirectTo = event.detail?.redirectTo;

      if (redirectTo) {
        this.#log(`Gate unlocked, redirecting to ${redirectTo}`);
        window.location.assign(redirectTo);
        return;
      }

      this.#log("Gate unlocked, revealing page content");
      this.#revealPage();
    };

    /**
     * Removes the body lock so #layout-content-container is visible, focuses main,
     * and removes this element from the document.
     */
    #revealPage() {
      document.body.classList.remove("page-gate--locked");

      const main = document.getElementById("main-content");
      if (main) {
        if (!main.hasAttribute("tabindex")) {
          main.setAttribute("tabindex", "-1");
        }
        main.focus({ preventScroll: true });
      }

      this.remove();

      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    }

    /** @param {string} key @returns {string|null} */
    #getStorage(key) {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    }

    /** @param {string} key @param {string} value */
    #setStorage(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch {
        this.#log("localStorage write failed");
      }
    }

    /** @param {string} message */
    #log(message) {
      if (!this.#debug) return;
      console.log(`[page-gate-container:${this.#data.gate_id}]`, message);
    }
  }

  customElements.define("page-gate-container", PageGateContainer);
}

/* =============================================================================
   PageGate
   ============================================================================= */

if (!customElements.get("page-gate")) {
  class PageGate extends HTMLElement {
    static #PASSWORD_ERROR_DEBOUNCE_MS = 300;

    /** @type {string|null} */
    #password = null;

    /** @type {boolean} */
    #debug = false;

    /** @type {ReturnType<typeof setTimeout>|null} */
    #passwordErrorDebounce = null;

    /** @type {HTMLInputElement|null} */
    #passwordInputEl = null;

    /** @type {HTMLElement|null} */
    #passwordErrorEl = null;

    connectedCallback() {
      this.#debug = this.dataset.debug === "true";
      this.#scrubPassword();

      const form = this.querySelector("form");

      if (form) {
        const isContactForm = this.hasAttribute("data-contact-form");

        form.addEventListener("submit", (e) => {
          e.preventDefault();
          const redirectTo = this.dataset.returnTo || "";

          if (isContactForm) {
            this.#handleContactFormSubmit(form, redirectTo);
            return;
          }

          if (this.#password != null) {
            const pwdInput =
              this.#passwordInputEl ||
              form.querySelector("[data-password-input]");
            if (!pwdInput || !this.#passwordMatches(pwdInput.value)) {
              this.#applyPasswordErrorUI();
              pwdInput?.focus();
              return;
            }
          }

          this.#log("Gate passed, dispatching unlock event");
          this.dispatchEvent(
            new CustomEvent("page-gate:unlocked", {
              bubbles: true,
              detail: { redirectTo },
            }),
          );
        });
      }
    }

    disconnectedCallback() {
      if (this.#passwordErrorDebounce != null) {
        clearTimeout(this.#passwordErrorDebounce);
        this.#passwordErrorDebounce = null;
      }
    }

    /**
     * Submits the contact form email to Klaviyo's client subscription API
     * instead of the broken Shopify /contact endpoint. Falls back to
     * a direct unlock if Klaviyo credentials are missing.
     * @param {HTMLFormElement} form
     * @param {string} redirectTo
     */
    async #handleContactFormSubmit(form, redirectTo) {
      const emailInput = form.querySelector("[data-email-input]");
      const email = emailInput?.value?.trim();

      if (!email) {
        emailInput?.focus();
        return;
      }

      const companyId = this.dataset.klaviyoCompanyId;
      const listId = this.dataset.klaviyoListId;

      if (!companyId || !listId) {
        this.#log("Klaviyo credentials missing, unlocking directly");
        this.dispatchEvent(
          new CustomEvent("page-gate:unlocked", {
            bubbles: true,
            detail: { redirectTo },
          }),
        );
        return;
      }

      const submitBtn = form.querySelector("[data-submit-button]");
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute("aria-disabled", "true");
      }

      try {
        const payload = {
          data: {
            type: "subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: { email },
                },
              },
              custom_source: this.dataset.pageGateTitle,
            },
            relationships: {
              list: {
                data: { type: "list", id: listId },
              },
            },
          },
        };

        this.#log(`Subscribing ${email} to Klaviyo list ${listId}`);

        const res = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
          {
            method: "POST",
            headers: {
              revision: "2024-06-15",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
        );

        if (!res.ok && res.status !== 202) {
          throw new Error(`Klaviyo subscribe failed: ${res.status}`);
        }

        this.#log("Klaviyo subscription successful, unlocking gate");
        this.dispatchEvent(
          new CustomEvent("page-gate:unlocked", {
            bubbles: true,
            detail: { redirectTo },
          }),
        );
      } catch (err) {
        console.error("[page-gate] Klaviyo submit error:", err);
        this.#log("Klaviyo failed, unlocking gate as fallback");
        this.dispatchEvent(
          new CustomEvent("page-gate:unlocked", {
            bubbles: true,
            detail: { redirectTo },
          }),
        );
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.setAttribute("aria-disabled", "false");
        }
      }
    }

    /**
     * Reads the password from the submit button's data attribute,
     * stores it in memory, scrubs it from the DOM, and sets up an
     * input listener that enables the button when the value matches.
     */
    #scrubPassword() {
      const btn = this.querySelector("[data-submit-button][data-password]");
      if (!btn) return;

      this.#password = String(btn.dataset.password ?? "").toLowerCase();
      btn.removeAttribute("data-password");
      this.#log("Password scrubbed from DOM");

      const input = this.querySelector("[data-password-input]");
      if (!input) return;

      this.#passwordInputEl = input;
      this.#passwordErrorEl = this.querySelector("#page-gate-password-error");

      input.addEventListener("input", () => {
        const isLocked = !this.#passwordMatches(input.value);
        btn.disabled = isLocked;
        btn.setAttribute("aria-disabled", String(isLocked));
        this.#schedulePasswordErrorUI();
      });

      input.addEventListener("blur", () => {
        this.#applyPasswordErrorUI();
      });

      this.#applyPasswordErrorUI();
    }

    /** Debounced: error text and aria-invalid only; button state updates synchronously on input. */
    #schedulePasswordErrorUI() {
      if (this.#passwordErrorDebounce != null) {
        clearTimeout(this.#passwordErrorDebounce);
      }
      this.#passwordErrorDebounce = setTimeout(() => {
        this.#passwordErrorDebounce = null;
        this.#applyPasswordErrorUI();
      }, PageGate.#PASSWORD_ERROR_DEBOUNCE_MS);
    }

    /** Case-insensitive match for promotional access codes. */
    #passwordMatches(entered) {
      if (this.#password == null) return false;
      return entered.toLowerCase() === this.#password;
    }

    /** Show error when value is non-empty and does not match; hide otherwise. */
    #applyPasswordErrorUI() {
      const input = this.#passwordInputEl;
      if (!input || this.#password == null) return;

      const value = input.value;
      const mismatch = value.length > 0 && !this.#passwordMatches(value);
      const errorEl = this.#passwordErrorEl;

      if (mismatch) {
        if (errorEl) errorEl.hidden = false;
        input.setAttribute("aria-invalid", "true");
      } else {
        if (errorEl) errorEl.hidden = true;
        input.removeAttribute("aria-invalid");
      }
    }

    /** @param {string} message */
    #log(message) {
      if (!this.#debug) return;
      console.log(`[page-gate:${this.dataset.pageGateId}]`, message);
    }
  }

  customElements.define("page-gate", PageGate);
}
